import { Link, useSearch } from '@tanstack/react-router';
import {
  type ColumnFiltersState,
  createColumnHelper,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import { format, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import * as React from 'react';
import { useCallback, useMemo } from 'react';

import {
  type BulkPaymentHistoryByDateItem,
  useGetBulkPaymentHistoryByDate,
} from '~entities/payment/api/get-bulk-payment-history-by-date';
import { useCurrentStudio } from '~entities/studios/model/studio-store';

import {
  EnrollioTable,
  EnrollioTableColumnToggle,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table';
import { Button } from '~shared/shadcn/ui/button';

const DATE_DISPLAY_FORMAT = 'MMM dd, yyyy, hh:mm a';

const columnHelper = createColumnHelper<BulkPaymentHistoryByDateItem>();

export const StudentChargeHistory = () => {
  const { id } = useSearch({
    from: '/_auth/_layout/payment/student-charge-history',
  });
  const { data, isLoading, isError } = useGetBulkPaymentHistoryByDate(id);
  const currentStudio = useCurrentStudio();
  const studioTimezone = currentStudio?.timezone;

  const formatDateForDisplay = useCallback(
    (iso: string | null | undefined) => {
      if (!iso) return '';
      try {
        if (studioTimezone) {
          try {
            return formatInTimeZone(iso, studioTimezone, DATE_DISPLAY_FORMAT);
          } catch {
            /* empty */
          }
        }
        return format(parseISO(iso), DATE_DISPLAY_FORMAT);
      } catch {
        return iso;
      }
    },
    [studioTimezone],
  );

  const cols = useMemo(
    () => [
      columnHelper.display({
        id: 'rowNumber',
        cell: ({ row }) => <div>{row.index + 1}</div>,
      }),
      columnHelper.accessor('name', {
        id: 'Name',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Name'
            disableSorting
          />
        ),
        cell: (info) => {
          const { name, studentId } = info.row.original;
          if (!studentId) {
            return <div className='px-4'>{name}</div>;
          }
          return (
            <Button
              variant='link'
              className='px-4 text-custom-secondary-1'
              asChild
            >
              <Link
                to='/students/$studentId/new-payments'
                params={{ studentId }}
              >
                {name}
              </Link>
            </Button>
          );
        },
      }),
      columnHelper.accessor('amount', {
        id: 'Amount',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Amount'
            disableSorting
          />
        ),
        cell: (info) => (
          <div className='px-4'>{Number(info.getValue()).toFixed(2)}</div>
        ),
      }),
      columnHelper.accessor('dateProcessed', {
        id: 'Date Processed',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Date Processed'
            disableSorting
          />
        ),
        cell: (info) => {
          const formatted = formatDateForDisplay(info.getValue());
          return <div className='px-4'>{formatted ? formatted : '—'}</div>;
        },
      }),
      columnHelper.accessor('paidDate', {
        id: 'Paid Date',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Paid Date'
            disableSorting
          />
        ),
        cell: (info) => {
          return (
            <div className='px-4'>
              {formatDateForDisplay(info.getValue() ?? '—')}
            </div>
          );
        },
      }),
      columnHelper.accessor('status', {
        id: 'Status',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Status'
            disableSorting
          />
        ),
        cell: (info) => (
          <div className='px-4 capitalize'>{info.getValue()}</div>
        ),
      }),
      columnHelper.accessor('failureReason', {
        id: 'Failure Reason',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Failure Reason'
            disableSorting
          />
        ),
        cell: (info) => {
          const reason = info.getValue()?.trim();
          return (
            <div className='max-w-xs px-4 text-sm'>
              {reason ? <span className='text-red-600'>{reason}</span> : '—'}
            </div>
          );
        },
      }),
    ],
    [formatDateForDisplay],
  );

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});

  if (isLoading) return <div>Loading...</div>;
  if (isError) return <div>Error loading data.</div>;

  return (
    <EnrollioTable
      ariaLabel='Student charge history'
      totalLabel='Charges'
      filterHeader={(table) => (
        <EnrollioTableSearch
          placeholder='Search students...'
          value={
            (table.getColumn('Name')?.getFilterValue() as string | undefined) ??
            ''
          }
          onChange={(value) => {
            table.getColumn('Name')?.setFilterValue(value);
          }}
        />
      )}
      toolbar={(table) => <EnrollioTableColumnToggle table={table} />}
      columns={cols}
      data={data ?? []}
      sorting={sorting}
      onSortingChange={setSorting}
      columnFilters={columnFilters}
      onColumnFiltersChange={setColumnFilters}
      columnVisibility={columnVisibility}
      onColumnVisibilityChange={setColumnVisibility}
      enableRowSelection={true}
      showSelectionCount
    />
  );
};
